/**
 * Regroupement des séries d'une séance, pour l'affichage
 * (`ARCHITECTURE_V2.md` §4.3 et §4.5).
 *
 * 🔴 **L'écran montre la séance ENTIÈRE, une carte par exercice** — demandé
 * après usage sur l'appareil *(29/08/2026)*, sur le modèle de Hevy. Le premier
 * jet n'ouvrait qu'**une série à la fois**, au motif que le §12.3 veut un écran
 * « utilisable d'une main » et qu'afficher trente formulaires ferait chercher le
 * bon à chaque série.
 *
 * **Le raisonnement était faux, et l'usage l'a montré.** Ce qu'on veut voir en
 * salle, ce n'est pas « la prochaine chose à faire » — on la connaît, on vient
 * de la lire — c'est **où l'on en est**. Combien il reste, ce qui vient après,
 * ce qu'on a mis à la série précédente. Une séance affichée série par série
 * oblige à se souvenir de tout ce qu'elle cache.
 *
 * ⚠️ **Conséquence à tenir : il n'y a plus de « série courante ».** N'importe
 * quelle série peut être validée à n'importe quel moment, ce qui est d'ailleurs
 * ce qu'on fait vraiment (on saute une série, on y revient, on inverse deux
 * exercices). Tout ce qui reposait sur « la première `pending` » a disparu.
 */

import type { ActualSetDTO } from '../../../../shared/types';

/**
 * Un exercice de la séance, et ses séries.
 *
 * ⚠️ **La clé combine le bloc ET l'exercice.** Le même exercice peut revenir
 * dans deux blocs d'une même séance (un développé couché en échauffement puis
 * en travail) : les fondre donnerait une carte dont les séries viennent de deux
 * endroits, avec deux repos différents.
 */
export interface SessionGroup {
  key: string;
  blockKey: string | null;
  exerciseClientId: string | null;
  exerciseName: string;
  mediaKey: string | null;
  imageCount: number;
  /** Rang du groupe dans la séance — le plus petit rang de ses séries. */
  position: number;
  /**
   * Lettre du bloc quand il en compte plusieurs — « A », « B »…
   *
   * 🔴 **Une lettre, jamais le mot « superset » ou « circuit ».** Le §4.2 ne
   * copie sur la séance que ce qui compte, et la **nature** du bloc n'en fait
   * pas partie : `actual_set` porte `block_key`, pas `kind`. Aller la chercher
   * dans la routine dirait quelque chose de possiblement **faux** d'une séance
   * passée — la routine a pu changer, ou disparaître. La lettre, elle, énonce
   * le seul fait que la séance connaisse d'elle-même : ces exercices vont
   * ensemble.
   */
  badge: string | null;
  sets: ActualSetDTO[];
}

const cle = (set: ActualSetDTO): string =>
  `${set.blockKey ?? '∅'}::${set.exerciseClientId ?? set.exerciseName}`;

/**
 * Groupe les séries par exercice, dans l'ordre où on les fait.
 *
 * ⚠️ **Ce regroupement par exercice ne suffit pas pour un enchaînement** — cf.
 * `groupBlocks` plus bas, et le retour d'usage qui l'a imposé. Il reste la
 * brique de base : `groupBlocks` s'en sert, puis range les séries par **tour**
 * quand un bloc porte plusieurs exercices.
 */
export function groupSets(sets: readonly ActualSetDTO[]): SessionGroup[] {
  const groupes = new Map<string, SessionGroup>();

  for (const set of sets) {
    const k = cle(set);
    const connu = groupes.get(k);
    if (connu) {
      connu.sets.push(set);
      connu.position = Math.min(connu.position, set.position);
      /* La vignette peut n'être connue que d'une réponse serveur ultérieure. */
      if (connu.mediaKey === null && set.exerciseMediaKey !== null) {
        connu.mediaKey = set.exerciseMediaKey;
        connu.imageCount = set.exerciseImageCount;
      }
    } else {
      groupes.set(k, {
        key: k,
        blockKey: set.blockKey,
        exerciseClientId: set.exerciseClientId,
        exerciseName: set.exerciseName,
        mediaKey: set.exerciseMediaKey,
        imageCount: set.exerciseImageCount,
        position: set.position,
        badge: null,
        sets: [set],
      });
    }
  }

  const liste = [...groupes.values()].sort((a, b) => a.position - b.position);
  for (const groupe of liste) {
    groupe.sets.sort((a, b) => a.setIndex - b.setIndex || a.position - b.position);
  }

  /*
   * La lettre n'est posée que sur les blocs qui comptent plusieurs exercices :
   * un exercice seul n'a rien à relier, et lui coller un « A » ferait croire à
   * un groupement qui n'existe pas.
   */
  const parBloc = new Map<string, SessionGroup[]>();
  for (const groupe of liste) {
    if (groupe.blockKey === null) continue;
    const lot = parBloc.get(groupe.blockKey) ?? [];
    lot.push(groupe);
    parBloc.set(groupe.blockKey, lot);
  }

  let lettre = 0;
  for (const groupe of liste) {
    if (groupe.blockKey === null) continue;
    const lot = parBloc.get(groupe.blockKey);
    if (!lot || lot.length < 2) continue;
    if (lot[0] === groupe) {
      lettre += 1;
      const marque = String.fromCharCode(64 + lettre);
      for (const membre of lot) membre.badge = marque;
    }
  }

  return liste;
}

/**
 * Faut-il déclencher le repos après avoir validé cette série ?
 *
 * 🔴 **Le repos est une propriété du BLOC, pas de la ligne** (§4.3) : « dans un
 * superset, valider la première série ne déclenche aucun repos ». La règle
 * survit intacte au passage à l'affichage Hevy — elle est seulement **relue
 * autrement**, puisqu'il n'y a plus d'ordre imposé.
 *
 * Le tour est fini quand **aucun autre exercice du même bloc** n'a de série
 * encore à faire à ce rang ou avant. Tant qu'il en reste une, on enchaîne :
 * c'est très exactement ce qu'on fait en salle.
 *
 * ⚠️ **`<=` et non `===`.** Si l'on a sauté la série 1 de l'exercice B et qu'on
 * valide la série 2 de A, il reste bien quelque chose à faire dans ce bloc
 * avant de se reposer. Comparer au seul rang égal ferait partir un repos au
 * milieu d'un tour incomplet.
 */
export function shouldRest(sets: readonly ActualSetDTO[], validee: ActualSetDTO): boolean {
  if (validee.blockKey === null) return true;

  const reste = sets.some(
    (set) =>
      set.blockKey === validee.blockKey &&
      set.clientId !== validee.clientId &&
      set.exerciseClientId !== validee.exerciseClientId &&
      set.status === 'pending' &&
      set.setIndex <= validee.setIndex,
  );
  return !reste;
}

/* ==========================================================================
 * Les blocs, et leurs tours (§4.3)
 * ======================================================================= */

/**
 * Un tour d'un enchaînement : une série de chaque exercice du bloc.
 *
 * ⚠️ **Un tour peut être incomplet** — une ligne plus courte que ses voisines
 * sort des derniers tours (c'est ce que produit `planOfRoutine`). On ne comble
 * pas : un tour à deux lignes au lieu de trois est ce qu'il faut faire.
 */
export interface SessionRound {
  index: number;
  sets: ActualSetDTO[];
}

/**
 * Un bloc de la séance — un exercice seul, ou plusieurs qui s'enchaînent.
 *
 * 🔴 **`rounds` n'est renseigné que pour un bloc à PLUSIEURS exercices**, et
 * c'est là tout le sujet du troisième retour d'usage *(29/08/2026)* : « affiche
 * les supersets d'une meilleure façon pour que l'ordre soit facilement
 * respecté ».
 *
 * La version précédente montrait deux cartes voisines reliées par une lettre.
 * La lettre dit *qu'ils vont ensemble* ; elle ne dit pas **dans quel ordre**.
 * Or un superset n'est pas un groupement, c'est une **séquence** : une série de
 * A, une série de B, repos, on recommence. En rangeant les séries par tour, la
 * carte affiche la séquence elle-même — il n'y a plus d'ordre à reconstituer de
 * tête, il est écrit.
 *
 * ⚠️ **Les cartes séparées n'étaient pas un mauvais rendu, c'était un mauvais
 * MODÈLE** : elles regroupaient par exercice une structure qui se parcourt par
 * tour. Le rendu suivait fidèlement un regroupement qui ne correspondait pas au
 * geste — et c'est pour cela qu'aucun ajustement de la carte n'aurait suffi.
 */
export interface SessionBlock {
  key: string;
  blockKey: string | null;
  position: number;
  exercises: SessionGroup[];
  /** `null` pour un exercice seul : il n'y a pas de tour à montrer. */
  rounds: SessionRound[] | null;
}

/**
 * Range la séance en blocs, et les blocs à plusieurs exercices en tours.
 *
 * ⚠️ **Le tour se lit sur `setIndex`**, qui compte les séries d'une ligne tous
 * tours confondus (cf. `sessionPlan.ts`). C'est ce qui permet de reconstituer
 * la séquence sans rien stocker de plus : la série 2 de A et la série 2 de B
 * appartiennent au même passage.
 */
export function groupBlocks(sets: readonly ActualSetDTO[]): SessionBlock[] {
  const exercices = groupSets(sets);

  const blocs = new Map<string, SessionBlock>();
  for (const groupe of exercices) {
    const cleBloc = groupe.blockKey ?? groupe.key;
    const connu = blocs.get(cleBloc);
    if (connu) {
      connu.exercises.push(groupe);
      connu.position = Math.min(connu.position, groupe.position);
    } else {
      blocs.set(cleBloc, {
        key: cleBloc,
        blockKey: groupe.blockKey,
        position: groupe.position,
        exercises: [groupe],
        rounds: null,
      });
    }
  }

  const liste = [...blocs.values()].sort((a, b) => a.position - b.position);

  for (const bloc of liste) {
    if (bloc.exercises.length < 2) continue;

    const rangs = [
      ...new Set(bloc.exercises.flatMap((exo) => exo.sets.map((set) => set.setIndex))),
    ].sort((a, b) => a - b);

    bloc.rounds = rangs.map((index) => ({
      index,
      /*
       * ⚠️ **Dans l'ordre des EXERCICES du bloc**, pas dans celui des
       * `position` : c'est l'ordre du plan, celui qu'on doit suivre. Trier par
       * position donnerait le même résultat tant que rien n'a bougé, et
       * divergerait dès qu'une série est ajoutée en cours de séance.
       */
      sets: bloc.exercises
        .map((exo) => exo.sets.find((set) => set.setIndex === index))
        .filter((set): set is ActualSetDTO => set !== undefined),
    }));
  }

  return liste;
}
